import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { PageHeader } from "../../components/StaffLayout";
import StatCard from "../../components/StatCard";
import { ChartBar, DownloadSimple, TrendUp, UsersThree, Wallet, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

export default function Reports() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(defaultFilters());
  const [doctorId, setDoctorId] = useState("");
  const [data, setData] = useState({
    patients: [],
    enquiries: [],
    appointments: [],
    treatments: [],
    invoices: [],
    doctors: [],
  });

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      api.get("/patients"),
      api.get("/enquiries"),
      api.get("/appointments"),
      api.get("/treatments"),
      api.get("/invoices"),
      api.get("/doctors"),
    ])
      .then(([patients, enquiries, appointments, treatments, invoices, doctors]) => {
        if (!mounted) return;
        setData({
          patients: patients.data || [],
          enquiries: enquiries.data || [],
          appointments: appointments.data || [],
          treatments: treatments.data || [],
          invoices: invoices.data || [],
          doctors: doctors.data || [],
        });
      })
      .catch((err) => {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const patientMap = useMemo(
    () => Object.fromEntries((data.patients || []).map((patient) => [patient.id, patient])),
    [data.patients]
  );

  const doctorOptions = useMemo(
    () => (data.doctors || []).map((doctor) => ({ id: doctor.id, name: doctor.name })),
    [data.doctors]
  );

  const scoped = useMemo(() => {
    const selectedDoctor = doctorId || null;
    const from = filters.from ? `${filters.from} 00:00:00` : null;
    const to = filters.to ? `${filters.to} 23:59:59` : null;

    const inRange = (value) => {
      if (!value) return false;
      const when = new Date(value).getTime();
      if (Number.isNaN(when)) return false;
      if (from && when < new Date(from).getTime()) return false;
      if (to && when > new Date(to).getTime()) return false;
      return true;
    };

    const matchesDoctor = (kind, item) => {
      if (!selectedDoctor) return true;
      if (kind === "patients") return item.primary_doctor_id === selectedDoctor;
      if (kind === "enquiries") {
        const doctor = doctorOptions.find((row) => row.id === selectedDoctor);
        return item.assigned_to === doctor?.name;
      }
      if (kind === "appointments" || kind === "treatments") return item.doctor_id === selectedDoctor;
      if (kind === "invoices") return patientMap[item.patient_id]?.primary_doctor_id === selectedDoctor;
      return true;
    };

    return {
      patients: data.patients.filter((item) => inRange(item.created_at) && matchesDoctor("patients", item)),
      enquiries: data.enquiries.filter((item) => inRange(item.created_at) && matchesDoctor("enquiries", item)),
      appointments: data.appointments.filter((item) => inRange(item.starts_at) && matchesDoctor("appointments", item)),
      treatments: data.treatments.filter((item) => inRange(item.performed_at || item.created_at) && matchesDoctor("treatments", item)),
      invoices: data.invoices.filter((item) => inRange(item.created_at) && matchesDoctor("invoices", item)),
    };
  }, [data, doctorId, doctorOptions, filters, patientMap]);

  const summary = useMemo(() => {
    const converted = scoped.enquiries.filter((item) => item.status === "converted").length;
    const outstanding = scoped.invoices.reduce((sum, item) => sum + Number(item.balance || 0), 0);
    const paid = scoped.invoices.reduce((sum, item) => sum + Number(item.paid || 0), 0);
    const treatmentRevenue = scoped.treatments.reduce((sum, item) => sum + Number(item.cost || 0), 0);
    const unassignedAppointments = scoped.appointments.filter((item) => item.assignment_state === "unassigned").length;
    const completedAppointments = scoped.appointments.filter((item) => item.status === "completed").length;

    return {
      patients: scoped.patients.length,
      enquiries: scoped.enquiries.length,
      converted,
      conversionRate: scoped.enquiries.length ? ((converted / scoped.enquiries.length) * 100).toFixed(1) : "0.0",
      appointments: scoped.appointments.length,
      completedAppointments,
      unassignedAppointments,
      treatmentRevenue,
      invoicePaid: paid,
      outstanding,
    };
  }, [scoped]);

  const sourceBreakdown = useMemo(() => aggregateCounts(scoped.enquiries, "source"), [scoped.enquiries]);
  const appointmentStatusBreakdown = useMemo(() => aggregateCounts(scoped.appointments, "status"), [scoped.appointments]);
  const doctorPerformance = useMemo(() => {
    const bucket = new Map();

    scoped.appointments.forEach((item) => {
      const name = item.doctor_obj?.name || item.doctor || "Unassigned";
      if (!bucket.has(name)) bucket.set(name, { name, appointments: 0, completed: 0, treatments: 0, treatmentRevenue: 0 });
      const row = bucket.get(name);
      row.appointments += 1;
      if (item.status === "completed") row.completed += 1;
    });

    scoped.treatments.forEach((item) => {
      const name = item.doctor || "Unassigned";
      if (!bucket.has(name)) bucket.set(name, { name, appointments: 0, completed: 0, treatments: 0, treatmentRevenue: 0 });
      const row = bucket.get(name);
      row.treatments += 1;
      row.treatmentRevenue += Number(item.cost || 0);
    });

    return Array.from(bucket.values()).sort((a, b) => b.treatmentRevenue - a.treatmentRevenue || b.appointments - a.appointments);
  }, [scoped.appointments, scoped.treatments]);

  const overdueInvoices = useMemo(
    () => scoped.invoices.filter((item) => Number(item.balance || 0) > 0).sort((a, b) => Number(b.balance || 0) - Number(a.balance || 0)).slice(0, 6),
    [scoped.invoices]
  );

  const recentEnquiries = useMemo(
    () => [...scoped.enquiries].sort((a, b) => sortByDateDesc(a.updated_at || a.created_at, b.updated_at || b.created_at)).slice(0, 6),
    [scoped.enquiries]
  );

  const exportCsv = () => {
    const rows = [
      ["Metric", "Value"],
      ["Patients Added", String(summary.patients)],
      ["Enquiries", String(summary.enquiries)],
      ["Converted Enquiries", String(summary.converted)],
      ["Conversion Rate", `${summary.conversionRate}%`],
      ["Appointments", String(summary.appointments)],
      ["Completed Appointments", String(summary.completedAppointments)],
      ["Unassigned Appointments", String(summary.unassignedAppointments)],
      ["Treatment Revenue", String(summary.treatmentRevenue)],
      ["Invoice Paid", String(summary.invoicePaid)],
      ["Outstanding", String(summary.outstanding)],
    ];
    const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "crm-report-summary.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div data-testid="reports-page">
      <PageHeader
        title="Detailed Reports"
        subtitle="A fuller view of leads, appointments, revenue, doctor performance, and follow-through."
        actions={
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-black text-white font-semibold rounded-sm px-4 py-2 text-sm"
          >
            <DownloadSimple size={16} weight="bold" /> Export summary
          </button>
        }
      />

      <div className="px-6 sm:px-10 py-8 space-y-8">
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">
            <ChartBar size={14} weight="bold" /> Report filters
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <FilterField label="From">
              <input
                type="date"
                value={filters.from}
                onChange={(e) => setFilters((current) => ({ ...current, from: e.target.value }))}
                className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 bg-white"
              />
            </FilterField>
            <FilterField label="To">
              <input
                type="date"
                value={filters.to}
                onChange={(e) => setFilters((current) => ({ ...current, to: e.target.value }))}
                className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 bg-white"
              />
            </FilterField>
            {user?.role !== "doctor" ? (
              <FilterField label="Doctor">
                <select
                  value={doctorId}
                  onChange={(e) => setDoctorId(e.target.value)}
                  className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 bg-white"
                >
                  <option value="">All doctors</option>
                  {doctorOptions.map((doctor) => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.name}
                    </option>
                  ))}
                </select>
              </FilterField>
            ) : null}
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilters(defaultFilters());
                  setDoctorId("");
                }}
                className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm font-semibold text-zinc-700 hover:border-zinc-900"
              >
                Reset filters
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <StatCard label="Patients Added" value={loading ? "..." : summary.patients} />
          <StatCard label="Enquiries" value={loading ? "..." : summary.enquiries} sub={`${summary.converted} converted`} />
          <StatCard label="Conversion Rate" value={loading ? "..." : `${summary.conversionRate}%`} accent sub="Lead to converted" />
          <StatCard label="Appointments" value={loading ? "..." : summary.appointments} sub={`${summary.unassignedAppointments} unassigned`} />
          <StatCard label="Treatment Revenue" value={loading ? "..." : formatCurrency(summary.treatmentRevenue)} />
          <StatCard label="Outstanding" value={loading ? "..." : formatCurrency(summary.outstanding)} sub={`${formatCurrency(summary.invoicePaid)} collected`} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel title="Enquiry Sources" icon={<UsersThree size={16} weight="bold" />}>
            <BreakdownList items={sourceBreakdown} empty="No enquiries in this range." />
          </Panel>
          <Panel title="Appointment Status Mix" icon={<TrendUp size={16} weight="bold" />}>
            <BreakdownList items={appointmentStatusBreakdown} empty="No appointments in this range." />
          </Panel>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <Panel title="Doctor Performance" icon={<ChartBar size={16} weight="bold" />} className="xl:col-span-2">
            {doctorPerformance.length === 0 ? (
              <EmptyState text="No doctor activity in this date range yet." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-zinc-500 border-b border-zinc-200">
                      <th className="py-2 font-semibold">Doctor</th>
                      <th className="py-2 font-semibold">Appointments</th>
                      <th className="py-2 font-semibold">Completed</th>
                      <th className="py-2 font-semibold">Treatments</th>
                      <th className="py-2 font-semibold text-right">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doctorPerformance.map((row) => (
                      <tr key={row.name} className="border-b border-zinc-100 last:border-b-0">
                        <td className="py-3 font-semibold text-zinc-900">{row.name}</td>
                        <td className="py-3">{row.appointments}</td>
                        <td className="py-3">{row.completed}</td>
                        <td className="py-3">{row.treatments}</td>
                        <td className="py-3 text-right font-semibold">{formatCurrency(row.treatmentRevenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel title="Invoice Attention" icon={<Wallet size={16} weight="bold" />}>
            {overdueInvoices.length === 0 ? (
              <EmptyState text="No outstanding invoices in this range." />
            ) : (
              <div className="space-y-3">
                {overdueInvoices.map((invoice) => (
                  <div key={invoice.id} className="border border-zinc-200 rounded-sm p-3">
                    <div className="font-semibold text-zinc-900">{invoice.patient?.name || "Unknown patient"}</div>
                    <div className="text-xs text-zinc-500 mt-1">Invoice #{invoice.invoice_number || invoice.id.slice(0, 8)}</div>
                    <div className="mt-2 flex items-center justify-between text-sm">
                      <span className="text-zinc-500">Outstanding</span>
                      <span className="font-semibold text-[#E60000]">{formatCurrency(invoice.balance)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel title="Recent Enquiries Requiring Attention" icon={<WarningCircle size={16} weight="bold" />}>
            {recentEnquiries.length === 0 ? (
              <EmptyState text="No enquiry activity in this range." />
            ) : (
              <div className="space-y-3">
                {recentEnquiries.map((enquiry) => (
                  <div key={enquiry.id} className="border border-zinc-200 rounded-sm p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold text-zinc-900">{enquiry.name}</div>
                      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">{enquiry.status}</span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">{enquiry.service || "General"} · {enquiry.source || "other"}</div>
                    <div className="text-xs text-zinc-500 mt-1">Assigned to {enquiry.assigned_to || "Unassigned"}</div>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel title="Report Notes" icon={<ChartBar size={16} weight="bold" />}>
            <div className="space-y-3 text-sm text-zinc-600">
              <p>This report uses the live CRM data already loaded in your current PHP setup, so doctor-level scoping still applies automatically.</p>
              <p>Date range affects each section by its natural timestamp: patient creation date, enquiry created date, appointment start date, treatment performed date, and invoice created date.</p>
              <p>If you want the next step, we can add printable monthly reports and CSV export for the full tables, not just the summary card section.</p>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">{label}</label>
      {children}
    </div>
  );
}

function Panel({ title, icon, className = "", children }) {
  return (
    <div className={`bg-white border border-zinc-200 rounded-sm p-5 ${className}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">
        {icon} {title}
      </div>
      {children}
    </div>
  );
}

function BreakdownList({ items, empty }) {
  if (items.length === 0) {
    return <EmptyState text={empty} />;
  }

  const total = items.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="space-y-3">
      {items.map((item) => {
        const pct = total > 0 ? (item.count / total) * 100 : 0;
        return (
          <div key={item.label}>
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-zinc-900">{item.label}</span>
              <span className="text-zinc-500">{item.count} · {pct.toFixed(0)}%</span>
            </div>
            <div className="mt-2 h-2 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#E60000]" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="text-sm text-zinc-500">{text}</div>;
}

function aggregateCounts(items, key) {
  const map = new Map();
  items.forEach((item) => {
    const raw = item?.[key];
    const label = String(raw || "Unknown")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
    map.set(label, (map.get(label) || 0) + 1);
  });

  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

function formatCurrency(value) {
  return `Rs ${Number(value || 0).toLocaleString("en-IN")}`;
}

function defaultFilters() {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 29);

  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

function sortByDateDesc(a, b) {
  return new Date(b).getTime() - new Date(a).getTime();
}

function csvEscape(value) {
  const stringValue = String(value ?? "");
  if (stringValue.includes(",") || stringValue.includes("\"") || stringValue.includes("\n")) {
    return `"${stringValue.replace(/"/g, "\"\"")}"`;
  }
  return stringValue;
}
